import { BrowserRouter, Routes, Route } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './theme-provider';
import { AppLayout } from './layout';
import { AuthGuard } from './auth-guard';
import { SessionProvider } from './session';
import { NotificationsProvider } from './notifications-store';
import { Toaster } from '@/components/ui/sonner';
import { Dashboard } from '@/pages/Dashboard';
import { Login } from '@/pages/Login';
import { Notifications } from '@/pages/Notifications';
import { Settings } from '@/pages/Settings';
import { Profile } from '@/pages/Profile';
import { Forbidden, NotFound, ServerError } from '@/pages/errors';
import { Scoring } from '@/pages/Scoring';
import { Candidates } from '@/pages/Candidates';
import { CandidateDetail } from '@/pages/CandidateDetail';
import { Modules } from '@/pages/Modules';
import { Questions } from '@/pages/Questions';
import { Matrix } from '@/pages/Matrix';
import { QcReview } from '@/pages/QcReview';
import { StartRun } from '@/pages/StartRun';
import { RunAssessment } from '@/pages/RunAssessment';
import { Result } from '@/pages/Result';
import { Results } from '@/pages/Results';
import { QuestionTypes } from '@/pages/QuestionTypes';
import { Analytics } from '@/pages/Analytics';
import { ItemAnalysis } from '@/pages/ItemAnalysis';
import { Support } from '@/pages/screens';

// TanStack Query everywhere for reads/mutations (build/01, build/10 §8), so
// mutation success can invalidate query keys and keep the UI honest.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {/* Session sits above the router: AuthGuard, the login screen and the
            topbar Sign out button all read the same one. */}
        <SessionProvider>
          {/* One notification list: the topbar popup and the Notifications page
              render the same state, so the bell's unread dot cannot disagree
              with the list behind it. */}
          <NotificationsProvider>
            <BrowserRouter>
              <Routes>
                {/* Guest routes render outside the console chrome. Login is the
                    start screen — AuthGuard sends every unauthenticated visitor
                    here, and Sign out returns to it. */}
                <Route path="/login" element={<Login />} />
                <Route path="/error/403" element={<Forbidden />} />
                <Route path="/error/404" element={<NotFound />} />
                <Route path="/error/500" element={<ServerError />} />
  
                <Route
                  element={
                    <AuthGuard>
                      <AppLayout />
                    </AuthGuard>
                  }
                >
                  {/* Overview */}
                  <Route index element={<Dashboard />} />
                  <Route path="analytics" element={<Analytics />} />
  
                  {/* Assessment Design */}
                  <Route path="modules" element={<Modules />} />
                  {/* More specific route first, or /questions would swallow it. */}
                  <Route path="questions/types" element={<QuestionTypes />} />
                  <Route path="questions" element={<Questions />} />
                  <Route path="scoring" element={<Scoring />} />
                  {/* M1. Its own screen rather than a tab, because an item
                      analysis is computed per module and needs its own filters
                      — see the comment at the top of the page. */}
                  <Route path="item-analysis" element={<ItemAnalysis />} />
                  <Route path="personas" element={<Matrix />} />
  
                  {/* Delivery */}
                  <Route path="candidates" element={<Candidates />} />
                  {/* The person-centred view (W1). Every other delivery screen
                      is scoped to a module; this one is scoped to a person. */}
                  <Route path="candidates/:candidateRef" element={<CandidateDetail />} />
                  <Route path="qc-review" element={<QcReview />} />
                  <Route path="take" element={<StartRun />} />
                  <Route path="run/:assessmentId" element={<RunAssessment />} />
                  <Route path="results" element={<Results />} />
                  <Route path="results/:resultId" element={<Result />} />
  
                  {/* Platform */}
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="support" element={<Support />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="profile" element={<Profile />} />
  
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
              <Toaster />
            </BrowserRouter>
          </NotificationsProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
